import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MessageSquare, Paperclip } from "lucide-react";
import { adminListAllMessageThreads } from "@/lib/portal/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/messages")({
  head: () => ({ meta: [{ title: "Messages — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminMessagesPage,
});

function AdminMessagesPage() {
  const fn = useServerFn(adminListAllMessageThreads);
  const q = useQuery({ queryKey: ["admin", "message-threads"], queryFn: () => fn(), refetchInterval: 15000 });

  const threads = q.data?.threads ?? [];
  const totalUnread = threads.reduce((n: number, t: any) => n + (t.unread || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Inbox</p>
          <h1 className="font-serif text-3xl text-white mt-1">Messages</h1>
          <p className="text-sm text-zinc-400 mt-1">All customer conversations in one place.</p>
        </div>
        {totalUnread > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-300/15 border border-amber-300/30 px-3 py-1 text-xs text-amber-200 font-semibold">
            {totalUnread} unread
          </span>
        )}
      </div>

      {q.isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-zinc-400" /></div>
      ) : threads.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center">
          <MessageSquare className="size-8 text-zinc-500 mx-auto mb-3" />
          <p className="text-zinc-400">No customer messages yet.</p>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {threads.map((t: any) => {
            const isUnread = t.unread > 0;
            const preview = (t.latest.body ?? "").trim();
            const hasAttach = Array.isArray(t.latest.attachments) && t.latest.attachments.length > 0;
            const who = t.latest.sender_role === "admin" ? "You" : (t.latest.sender_name || t.project.customer_name || "Customer");
            return (
              <li key={t.projectId}>
                <Link
                  to="/admin/customers/$projectId"
                  params={{ projectId: t.projectId }}
                  search={{ tab: "messages" } as any}
                  className="flex items-start gap-3 px-4 py-4 hover:bg-white/5 transition"
                >
                  <div className={`mt-1 size-9 rounded-full grid place-items-center shrink-0 ${isUnread ? "bg-amber-300 text-zinc-950" : "bg-white/5 text-zinc-400"}`}>
                    <MessageSquare className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className={`truncate text-sm ${isUnread ? "text-white font-semibold" : "text-zinc-200"}`}>
                        {t.project.customer_name || t.project.customer_email || "Customer"}
                        <span className="text-zinc-500 font-normal"> · {t.project.project_name || "Project"}</span>
                      </p>
                      <span className="text-[11px] text-zinc-500 shrink-0">
                        {new Date(t.latest.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className={`mt-0.5 text-sm truncate ${isUnread ? "text-zinc-200" : "text-zinc-400"}`}>
                      <span className="text-zinc-500">{who}:</span>{" "}
                      {preview || (hasAttach ? "(attachment)" : "(no content)")}
                      {hasAttach && preview && <Paperclip className="inline size-3 ml-1 text-zinc-500" />}
                    </p>
                  </div>
                  {isUnread && (
                    <span className="ml-2 shrink-0 inline-flex items-center justify-center rounded-full bg-amber-300 text-zinc-950 text-[11px] font-bold min-w-[22px] h-[22px] px-1.5">
                      {t.unread}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}